/*
 * e4e-motion.js
 * Brand-safe storefront motion:
 *   1. Scroll parallax for section media — drifts in BOTH scroll directions
 *      (offset derived from viewport position, not scroll velocity).
 *   2. Reveal on scroll — the entrance SHAPE and the child STAGGER are chosen
 *      per section in the theme editor and arrive here as data attributes
 *      (snippets/e4e-motion-attrs.liquid).
 *   3. Hover — marks a section's real blocks so the CSS can treat them.
 *
 * OWNERSHIP (see docs/motion-ownership.md). One element, one animation owner:
 *   [data-e4e-anim]  -> this file (explicit, merchant-chosen)
 *   .scroll-trigger  -> Dawn's animations.js
 *   legacy selectors -> this file, for longform bodies only, whose HTML comes
 *                       from page.content and so cannot carry an attribute
 * Never two owners on one element, and never an owner inside an owner.
 *
 * - No dependencies. Only on-screen elements are updated (IntersectionObserver
 *   + batched rAF).
 * - Fail-safe: if JS never runs, content stays visible — the hidden state in
 *   e4e-motion.css requires html.e4e-motion, which is added here. Blocks
 *   already in the first viewport are shown immediately, never hidden.
 * - Reveal and parallax are fully disabled under prefers-reduced-motion; hover
 *   marking still runs (it hides nothing, and the CSS stills it separately).
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var allowMotion = !reduce.matches;

  /* ---------------------------------------------------------------- parallax */
  var PARALLAX_SELECTOR = [
    '[data-e4e-parallax]',
    '.multicolumn-card__image-wrapper .media > img',
    '.image-with-text__media > img',
    '.collage__item .media > img'
  ].join(',');

  var items = [];
  var ticking = false;

  function decorateParallax() {
    var nodes = document.querySelectorAll(PARALLAX_SELECTOR);
    if (!nodes.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var rec = entry.target.__e4ePx;
          if (rec) rec.visible = entry.isIntersecting;
        });
        requestUpdate();
      },
      { rootMargin: '10% 0px 10% 0px', threshold: 0 }
    );
    nodes.forEach(function (el) {
      if (el.__e4ePx) return;
      el.classList.add('e4e-parallax');
      var rec = { el: el, visible: false };
      el.__e4ePx = rec;
      items.push(rec);
      io.observe(el);
    });
  }

  function update() {
    ticking = false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var mid = vh / 2;
    for (var i = 0; i < items.length; i++) {
      var rec = items[i];
      if (!rec.visible) continue;
      var rect = rec.el.getBoundingClientRect();
      var center = rect.top + rect.height / 2;
      var ratio = (center - mid) / (vh / 2 + rect.height / 2);
      if (ratio < -1) ratio = -1;
      if (ratio > 1) ratio = 1;
      rec.el.style.setProperty('--e4e-parallax', ratio.toFixed(3));
    }
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  /* ------------------------------------------------------------------ reveal */
  // Explicit, merchant-chosen opt-in.
  var OPTIN_SELECTOR = '[data-e4e-anim]';

  // Legacy targets. These predate the per-section picker and exist ONLY for
  // longform bodies, whose HTML lives in page.content and therefore cannot
  // carry an attribute. Scoped to .e4e-longform on purpose: unscoped, `.rte > p`
  // matched every product description and every article body -- including the
  // .rte that sections/main-article.liquid hands to Dawn, so the parent faded in
  // via .scroll-trigger while every child was independently held at opacity 0
  // by this file. `.e4e-diagram` was in this list and matches nothing in the
  // repo; it is removed rather than left as a trap for the first element to use
  // that very plausible class name.
  var LEGACY_SELECTOR = [
    '[data-e4e-reveal]',
    '.e4e-longform .band',
    '.e4e-longform > h2',
    '.e4e-longform > h3',
    '.e4e-longform > h6',
    '.e4e-longform > p',
    '.e4e-longform > ul',
    '.e4e-longform > ol',
    '.e4e-longform > blockquote',
    '.e4e-longform > .lede',
    '.e4e-longform > .stats'
  ].join(',');

  var REVEAL_SELECTOR = OPTIN_SELECTOR + ',' + LEGACY_SELECTOR;
  // What counts as "already owned" when checking ancestors. Includes Dawn's
  // class: the old guard tested only REVEAL_SELECTOR, so a legacy target inside
  // a .scroll-trigger ancestor was never recognised as already-animating.
  var OWNED_SELECTOR = REVEAL_SELECTOR + ',.scroll-trigger';

  var STAGGER_CAP = 24; // a 60-item grid must not cascade for five seconds
  var revealIO = null;

  // The viewport height, or 0 if it cannot be measured. Every decision about
  // what to hide is made against this, so when it is unmeasurable the ONLY safe
  // answer is "hide nothing" -- a wrong guess here leaves real content at
  // opacity 0 forever. Seen for real in an automation surface reporting
  // innerHeight === 0; the same shape covers a zero-height iframe or an
  // element measured before layout.
  function viewportH() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  // Show everything, immediately and permanently. The escape hatch used
  // whenever we cannot safely decide, so the failure mode is "no animation"
  // rather than "invisible page".
  function revealEverything() {
    document.querySelectorAll('.e4e-reveal').forEach(function (el) {
      el.classList.add('e4e-in', 'e4e-anim-done');
    });
  }

  // Precedence: an explicit opt-in OWNS its element, so Dawn's system is
  // stripped from it. Two hiding systems on one node means two stacked
  // opacity:0s and two stacked transforms. The Liquid edits already drop the
  // class at source for every section we touched; this catches app blocks and
  // any section that gets re-Dawned by a theme update.
  function claim(el) {
    el.classList.remove(
      'scroll-trigger',
      'animate--fade-in',
      'animate--slide-in',
      'scroll-trigger--offscreen',
      'scroll-trigger--cancel'
    );
  }

  // A "block row" is a container whose children are all the same kind of thing:
  // same tag, same first class token. .e4e-feature__cards (all DIV.e4e-card),
  // .e4e-stats__inner (all DIV.e4e-stats__item), .multicolumn-list (all
  // LI.multicolumn-list__item). A content rail is NOT: .e4e-feature__inner is
  // P + H2 + P + DIV. First class TOKEN, not the whole className, so
  // e4e-card--refuel and e4e-card--recharge read as the same kind.
  function isBlockRow(el) {
    var kids = el.children;
    if (kids.length < 2) return false;
    var sig = function (n) {
      return n.tagName + '.' + ((n.getAttribute('class') || '').trim().split(/\s+/)[0] || '');
    };
    var first = sig(kids[0]);
    for (var i = 1; i < kids.length; i++) if (sig(kids[i]) !== first) return false;
    return true;
  }

  var NOT_RENDERED = { SCRIPT: 1, STYLE: 1, TEMPLATE: 1, NOSCRIPT: 1, LINK: 1 };

  // A stagger cascades a section's CONTENT BLOCKS. A decorative layer is not one,
  // and making it a target is destructive rather than merely pointless: the target
  // gets `opacity: 0` then `opacity: 1` from e4e-motion.css, and `.e4e-reveal.e4e-in`
  // (0,3,1) outranks any single-class rule the layer styles itself with. That is how
  // `.e4e-band__ghost { opacity: .07 }` ended up rendering as a full-strength E4E
  // watermark. `.e4e-anim-done` clears transform and will-change afterwards, NOT
  // opacity, so the clobber is permanent.
  //
  // The pixel gates cannot catch this -- SETTLE_CSS forces every reveal hook to
  // `opacity: 1 !important` on measurement passes, so a clobbered layer screenshots
  // exactly as it does when broken. The `anim-decor-target` rule in visual-qa.mjs
  // asserts on the CLASS instead, which the freeze cannot mask.
  //
  // Costs one getComputedStyle + one getBoundingClientRect per candidate child, once
  // per section at init (a handful of children each). Do not "optimise" it away: the
  // out-of-flow and zero-area tests are the two that need layout.
  function isContentBlock(el) {
    if (NOT_RENDERED[el.tagName]) return false;
    if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
    var pos = getComputedStyle(el).position;
    if (pos === 'absolute' || pos === 'fixed') return false; // a layer, not a block
    // An element that OWNS an out-of-flow flyout must never take a transform: a
    // transform makes it the containing block for its absolutely positioned
    // descendants. Dawn sets `.mega-menu { position: static }` precisely so the
    // full-bleed .mega-menu__content can escape to the header-wrapper. The header
    // carries data-e4e-anim-hover="lift", so .header__inline-menu became an
    // .e4e-anim-child and hovering the nav applied translate3d(0,-4px,0) -- which
    // re-anchored the flyout to the 542px menu strip. The mega-menu grid resolved
    // to `580px 0px`, the link column collapsed to zero width, and the Essential
    // Workers panel rendered as 3247px of one-character-per-line text with all six
    // persona links invisible. Same class of defect as the decorative-layer
    // exclusions above: the motion group claiming something that is not a card.
    if (el.querySelector('.mega-menu__content')) return false;
    // The header's right-hand cluster is a utility bar, not a card. Because the
    // header carries data-e4e-anim-hover="lift", .header__icons became an
    // .e4e-anim-child and hovering the Score CTA translated the whole CTA +
    // Log-In + cart group up 4px -- the CTA looked like it dragged the account
    // section with it. Same exclusion class as the mega-menu above.
    if (el.classList.contains('header__icons')) return false;
    // A tab panel's visibility belongs to its own controller, not to the motion
    // group. Five of six are `hidden` at any moment, but the markup ships none of
    // them hidden -- that is the no-JS contract in sections/e4e-timeline.liquid --
    // so at decorate time all six still look like a uniform card row and
    // staggerTargets expands them. The reveal state then puts opacity 0/1 on
    // elements the tablist is about to show and hide, and `.e4e-anim-done` never
    // clears opacity, so the clobber is permanent. A one-of-six stack is one
    // content block: let the wrapper animate as a unit. Same exclusion class as
    // the two above -- the motion group claiming something that is not a card.
    if (el.getAttribute('role') === 'tabpanel') return false;
    var r = el.getBoundingClientRect();
    // Zero-area covers display:none, empty anchor spans, and a <picture> whose only
    // child is an absolutely-positioned image (the hero's LCP media).
    return r.width > 0 && r.height > 0;
  }

  // The elements a section should stagger. Descends the single-child chain to
  // the content rail (.e4e-feature -> .e4e-feature__inner), then returns either that
  // rail's children, or -- where a child is itself a block row -- that row's
  // items, so a card grid cascades card by card rather than as one lump.
  //
  // The descent deliberately walks RAW children, not content blocks. Descending on
  // the filtered set would make .e4e-band (ghost + inner) and .e4e-hero (three decor
  // layers + content) look single-child, drop into their copy columns, and start
  // hiding <h1>s independently -- which docs/motion-ownership.md forbids. Filter the
  // ANSWER, never the path to it: a rail left with fewer than two content blocks
  // returns them anyway, and decorateReveal's `kids.length > 1` test falls back to
  // animating the wrapper as one unit.
  function staggerTargets(root) {
    var rail = root;
    var hops = 0;
    while (rail.children.length === 1 && hops++ < 3) rail = rail.firstElementChild;

    if (isBlockRow(rail)) return Array.prototype.slice.call(rail.children).filter(isContentBlock);

    var out = [];
    Array.prototype.forEach.call(rail.children, function (kid) {
      if (isBlockRow(kid)) out.push.apply(out, Array.prototype.slice.call(kid.children));
      else out.push(kid);
    });
    return out.filter(isContentBlock);
  }

  function onEntranceEnd(e) {
    if (e.target !== this) return; // ignore transitions bubbling up from children
    if (e.propertyName !== 'opacity' && e.propertyName !== 'transform') return;
    this.classList.add('e4e-anim-done');
    this.removeEventListener('transitionend', onEntranceEnd);
  }

  function reveal(el) {
    var targets = el.__e4eTargets || [el];
    targets.forEach(function (t) {
      t.classList.add('e4e-in');
      // Drop the compositing hint once the entrance is over, so the element
      // stops being a containing block for fixed/sticky descendants.
      t.addEventListener('transitionend', onEntranceEnd);
      var d = parseInt(t.style.getPropertyValue('--e4e-reveal-delay'), 10) || 0;
      setTimeout(function () {
        t.classList.add('e4e-anim-done');
      }, d + 1400);
    });
    if (revealIO) revealIO.unobserve(el);
  }

  function decorateReveal() {
    var nodes = document.querySelectorAll(REVEAL_SELECTOR);
    if (!nodes.length) return;
    var vh = viewportH();
    // No IntersectionObserver, or no measurable viewport: never enter the
    // hidden state at all. Degrading to a static page is correct; hiding
    // content we can then never prove is on screen is not.
    if (!vh || typeof IntersectionObserver === 'undefined') return;
    if (!revealIO) {
      revealIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) reveal(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    var stagger = 0;
    nodes.forEach(function (el) {
      if (el.__e4eReveal) return;
      if (el.hasAttribute('data-e4e-anim')) claim(el); // precedence
      if (el.classList.contains('scroll-trigger')) return; // Dawn owns it
      // Skip elements nested inside another animating block — the ancestor
      // animates them in as one unit (avoids double-hiding).
      if (el.parentElement && el.parentElement.closest(OWNED_SELECTOR)) return;
      el.__e4eReveal = true;

      // Choose the hidden set. When a stagger is asked for, the CHILDREN are
      // the reveal targets and the wrapper is NOT hidden; otherwise the wrapper
      // is the single target. Never both — two nested hidden states multiply
      // their opacities and compound their transforms.
      var step = parseInt(el.getAttribute('data-e4e-anim-stagger'), 10) || 0;
      var targets = [el];
      if (step > 0) {
        var kids = staggerTargets(el);
        if (kids.length > 1) {
          targets = kids;
          kids.forEach(function (kid, i) {
            kid.style.setProperty('--e4e-reveal-delay', Math.min(i, STAGGER_CAP) * step + 'ms');
          });
        }
      }
      el.__e4eTargets = targets;
      targets.forEach(function (t) {
        t.classList.add('e4e-reveal');
      });

      if (el.getBoundingClientRect().top < vh * 0.95) {
        // In/near the first screen: animate in on load. A section with its own
        // internal stagger keeps it; only single-target legacy nodes get the
        // element-level cascade, so the first screen never arrives as one slab.
        if (targets.length === 1 && !el.hasAttribute('data-e4e-anim')) {
          el.style.setProperty('--e4e-reveal-delay', stagger * 70 + 'ms');
          stagger++;
        }
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            reveal(el);
          });
        });
      } else {
        // Observe the WRAPPER and let reveal() fan out to its targets — that is
        // what makes a stagger read as a stagger, rather than as N independent
        // entrances scattered down a tall section.
        revealIO.observe(el);
      }
    });
  }

  // Fail-safe: never leave on-screen content hidden, even if rAF/IO are paused
  // (e.g. loaded while the tab is unfocused). Timers fire regardless.
  function revealInView() {
    var vh = viewportH();
    // Unmeasurable viewport: `top < 0` would hide everything below the fold
    // permanently. Show it all instead.
    if (!vh) return revealEverything();
    document.querySelectorAll('.e4e-reveal:not(.e4e-in)').forEach(function (el) {
      if (el.getBoundingClientRect().top < vh * 0.95) reveal(el);
    });
  }

  /* ------------------------------------------------------------------- hover */
  // Marks a section's real blocks so the CSS can scope the hover treatment to
  // them instead of to every descendant. Hides nothing, so it runs even under
  // reduced motion (e4e-motion.css stills the movement separately).
  function decorateHover() {
    document.querySelectorAll('[data-e4e-anim-hover]').forEach(function (el) {
      if (el.__e4eHover) return;
      el.__e4eHover = true;
      var kids = staggerTargets(el);
      if (kids.length < 2) return; // a single-block band is not a card grid
      kids.forEach(function (k) {
        k.classList.add('e4e-anim-child');
      });
    });
  }

  /* -------------------------------------------------------------------- init */
  function onScroll() {
    requestUpdate();
    revealInView(); // backup to IntersectionObserver
  }

  function init() {
    // Theme editor: never hide anything. Dawn does this with
    // scroll-trigger--design-mode (animations.js); without the equivalent a
    // merchant editing any of these sections watches their content vanish until
    // the observer happens to fire. Editing is not viewing.
    if (window.Shopify && window.Shopify.designMode) {
      decorateHover();
      return;
    }

    decorateHover(); // safe under reduced motion
    if (!allowMotion) return;

    document.documentElement.classList.add('e4e-motion');
    decorateParallax();
    decorateReveal();
    requestUpdate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('load', function () {
      setTimeout(revealInView, 60);
    });
    // Extra safety net in case load already fired or rAF was paused.
    setTimeout(revealInView, 1200);
    document.addEventListener('shopify:section:load', function () {
      decorateParallax();
      decorateReveal();
      decorateHover();
      requestUpdate();
      // A section re-rendered in the editor must never sit hidden while the
      // merchant is looking at it.
      setTimeout(revealInView, 50);
    });
  }

  reduce.addEventListener &&
    reduce.addEventListener('change', function (e) {
      allowMotion = !e.matches;
      if (e.matches) {
        document.documentElement.classList.remove('e4e-motion');
        items.forEach(function (rec) {
          rec.el.style.removeProperty('--e4e-parallax');
        });
      } else {
        document.documentElement.classList.add('e4e-motion');
        requestUpdate();
      }
    });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
